/**
 * ConnectionInfoDialog Component
 *
 * A small dialog that displays connection details and server information.
 * Shows server version (fetched dynamically), host, port, database, auth method, and login.
 */

import { useEffect } from 'react';
import { Server, Database, Key, User, Globe, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { useConnectionInfoDialogStore } from '../store/connectionInfoDialogStore';
import { fetchServerVersionInfo } from '../services/serverInfoService';
import { redactSecretText, safeConnectionName } from '../../../types/safeConnectionDisplay';
import { ConnectionEngineIcon, getEngineLabel } from './ConnectionEngineIcon';
import { connectionTargetLine } from '../utils/connectionServerLine';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
/**
 * Format the authentication method for display
 */
function formatAuthMethod(auth: string | undefined): string {
  if (!auth) return 'Not specified';
  switch (auth) {
    case 'SQL Server authentication':
      return 'SQL Server Auth';
    case 'Windows authentication':
      return 'Windows Auth';
    case 'Microsoft Entra ID (Device Code)':
      return 'Microsoft Entra ID (Device Code)';
    case 'Microsoft Entra ID (Interactive Browser)':
      return 'Microsoft Entra ID (Interactive Browser)';
    case 'Password':
      return 'Password';
    case 'No password':
      return 'No Password';
    default:
      return auth;
  }
}

/**
 * InfoRow component for displaying a label-value pair
 */
function InfoRow({
  icon: Icon,
  label,
  value,
  className = '',
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | undefined;
  className?: string;
}) {
  return (
    <div className={`flex items-start gap-3 ${className}`}>
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm font-medium break-all" title={value || 'Not specified'}>
          {value || 'Not specified'}
        </div>
      </div>
    </div>
  );
}

export function ConnectionInfoDialog() {
  const { isOpen, connection, serverInfo, closeDialog, setServerInfo } =
    useConnectionInfoDialogStore();

  // Fetch server version info when dialog opens
  useEffect(() => {
    if (!isOpen || !connection) return;

    let cancelled = false;

    async function fetchInfo() {
      try {
        const info = await fetchServerVersionInfo(
          connection!.id,
          connection!.engine || 'mssql',
          connection!.database
        );
        if (!cancelled) {
          setServerInfo({
            version: info.version,
            edition: info.edition,
            platform: info.platform,
            isLoading: false,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setServerInfo({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch server info',
          });
        }
      }
    }

    void fetchInfo();

    return () => {
      cancelled = true;
    };
  }, [isOpen, connection, setServerInfo]);

  if (!connection) return null;

  // Never raw server text: an aliasless legacy record can hold a descriptor with a wallet password (security slice).
  const displayName = safeConnectionName(connection);
  const engine = connection.engine || 'mssql';

  // Where it dials, as the connection cards say it: the file, the TNS alias or the descriptor's host / port / service
  // (never the descriptor, which can carry a wallet password), secrets typed into a server masked (C2 review C2-03).
  const serverAddress = connectionTargetLine(connection);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="sm:max-w-lg" data-testid="connection-info-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ConnectionEngineIcon engine={engine} size="md" />
            <span className="truncate">{displayName}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Server Version Section */}
          <div className="rounded-lg border bg-muted/30 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Info className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Server Version</span>
            </div>
            {serverInfo.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <RandomDotmSpinner size={16} />
                <span>Fetching server info...</span>
              </div>
            ) : serverInfo.error ? (
              <div className="text-sm text-destructive">{redactSecretText(serverInfo.error)}</div>
            ) : (
              <div className="space-y-1">
                <div className="text-sm">{serverInfo.version}</div>
                {serverInfo.edition && (
                  <div className="text-xs text-muted-foreground">{serverInfo.edition}</div>
                )}
                {serverInfo.platform && (
                  <div className="text-xs text-muted-foreground">{serverInfo.platform}</div>
                )}
              </div>
            )}
          </div>

          {/* Connection Details Section */}
          <div className="grid gap-3">
            <InfoRow icon={Globe} label="Engine" value={getEngineLabel(engine)} />
            <InfoRow icon={Server} label="Server" value={serverAddress} />
            {engine !== 'sqlite' && connection.port && (
              <InfoRow
                icon={Server}
                label="Port"
                value={String(connection.port)}
              />
            )}
            {connection.database && (
              <InfoRow icon={Database} label="Default Database" value={connection.database} />
            )}
            {engine !== 'sqlite' && (
              <>
                <InfoRow
                  icon={Key}
                  label="Authentication"
                  value={formatAuthMethod(connection.authentication)}
                />
                {connection.login && (
                  <InfoRow icon={User} label="Login" value={connection.login} />
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={closeDialog}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
